import React, { FC, useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import styled from '@emotion/styled';

import { useButton, useDispatch, useI18n, useLogger, useSelector, useStaffCan } from '~Hooks';
import { Box, Button, GreenButton } from '~Styles';
import { Input } from '~Styles/inputs';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import useModal from '~ComponentsImproved/Modal/useModal';
import SelectedUser from '~Components/LinkUser/SelectedUser';
import { GET_USER } from '~Components/LinkUser/api';
import * as actions from '~actions';
import {
  AssignMembershipPoints,
  AssignMembershipPointsVariables,
  GetUserEasyId,
  GetUserEasyIdVariables,
} from './__queries__';
import Loader from '~ComponentsImproved/Loader';

const GET_USER_EASY_ID = gql`
  query GetUserEasyId($userId: ID!) {
    userEasyId(userId: $userId) {
      success
      easyId
    }
  }
`;

const ASSIGN_MEMBERSHIP_POINTS = gql`
  mutation AssignMembershipPoints($venueId: ID, $easyId: String!, $amount: Int!) {
    assignMembershipPoints(venueId: $venueId, easyId: $easyId, amount: $amount) {
      success
      message
    }
  }
`;

const DetailWrapper = styled(Box)`
  flex: 1;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  border: 1px solid var(--colorBlueGray700);
  padding: 24px;
  align-self: flex-start;
`;

const EasyIdText = styled.span`
  color: var(--colorBlueGray200);
  font-weight: 400;
`;

const EasyIdSkeleton = styled.div`
  width: 60px;
  height: 16px;
  border-radius: 4px;
  background-color: var(--colorBlueGray900);
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.15;
    }
  }
`;

interface Props {
  userId: string;
}

/** Member details panel - shows SelectedUser with easyId and points assignment */
const MemberDetails: FC<Props> = ({ userId }) => {
  const { i18n } = useI18n();
  const logger = useLogger('member-details');
  const dispatch = useDispatch();
  const client = useApolloClient();
  const staffCan = useStaffCan();
  const venueId = useSelector((state) => state.venueId) as string;

  const [easyId, setEasyId] = useState<string | null>(null);
  const [pointsInput, setPointsInput] = useState('');

  const addPointsModal = useModal();

  // Fetch the user's easyId when userId changes
  const [fetchEasyId, easyIdQuery] = useLazyQuery<GetUserEasyId, GetUserEasyIdVariables>(
    GET_USER_EASY_ID,
    {
      fetchPolicy: 'no-cache',
    },
  );

  useEffect(() => {
    const load = async () => {
      setEasyId(null);
      const res = await fetchEasyId({ variables: { userId } });

      if (res.data?.userEasyId?.success) {
        setEasyId(res.data.userEasyId.easyId);
        logger.info('Fetched user easyId', { userId, easyId: res.data.userEasyId.easyId });
      }
    };

    load();
  }, [userId]);

  // Mutation for assigning points
  const [assignPoints, assignPointsMutation] = useMutation<
    AssignMembershipPoints,
    AssignMembershipPointsVariables
  >(ASSIGN_MEMBERSHIP_POINTS);

  const handleAssignPoints = async () => {
    const amount = parseInt(pointsInput) * 100;
    if (!easyId || isNaN(amount) || amount <= 0) {
      return;
    }

    logger.info('Assigning membership points', { venueId, easyId, amount });

    try {
      const res = await assignPoints({
        variables: { venueId, easyId, amount },
      });

      if (res.data?.assignMembershipPoints?.success) {
        logger.info('Points assigned successfully', { easyId, amount });
        dispatch(
          actions.addToast(`${pointsInput} ${i18n('CustomerForCheckout.Points')} added`, true),
        );

        setPointsInput('');
        addPointsModal.hide();

        // Refetch the GetUser query used by SelectedUser to update points display
        client.refetchQueries({
          include: [GET_USER],
        });
      } else {
        const message = res.data?.assignMembershipPoints?.message || 'Unknown error';
        logger.warn('Failed to assign points, message: ' + message, { easyId, amount });
        dispatch(actions.addToast(message, true));
      }
    } catch (error) {
      logger.error('Error assigning points', { errorObject: error });
      const message = error instanceof Error ? error.message : 'Unknown error';
      dispatch(actions.addToast(message, true));
    }
  };

  const addPointsButton = useButton({
    onClick: () => {
      setPointsInput('');
      addPointsModal.toggle();
    },
    disabled: !easyId || !staffCan.REFUND,
    disabledToast: !staffCan.REFUND ? i18n('MembershipPoints.Add.PermissionDenied') : undefined,
  });

  const confirmPointsButton = useButton({
    onClick: handleAssignPoints,
    disabled: !pointsInput || parseInt(pointsInput, 10) <= 0 || assignPointsMutation.loading,
  });

  return (
    <>
      <DetailWrapper display="flex" flexDirection="column" gap="small">
        {easyIdQuery.loading ? (
          <EasyIdSkeleton />
        ) : easyId ? (
          <Paragraph level={4} bottom={0} color="var(--colorBlueGray200)">
            <EasyIdText>#{easyId}</EasyIdText>
          </Paragraph>
        ) : null}

        <SelectedUser userId={userId} />

        <Box fluid display="flex" alignItems="flex-end" justifyContent="flex-end">
          <Button {...addPointsButton}>{i18n('MembershipPoints.Add')}</Button>
        </Box>
      </DetailWrapper>

      <Modal isShowing={addPointsModal.isShowing} hide={addPointsModal.hide}>
        <ModalWrapper width="400px">
          <Box display="flex" flexDirection="column" gap="small" fluid>
            <Input
              as="input"
              dark
              type="number"
              min="1"
              value={pointsInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPointsInput(e.target.value)}
              placeholder={`${i18n('MembershipPoints.Add.Placeholder')}...`}
              autoFocus
            />

            <GreenButton fluid {...confirmPointsButton}>
              {assignPointsMutation.loading ? (
                <Loader size="sm" color="var(--colorWhite)" />
              ) : (
                i18n('General.Confirm')
              )}
            </GreenButton>
          </Box>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default MemberDetails;
